import React, { useMemo, useState } from 'react';
import { Row, Col, Button, DatePicker, Select, Radio, Alert, Empty, Table, Card } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import ChartCard from '@/components/ChartCard';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSettlementData } from '@/api/settlement';
import { format2 } from '@/utils/charts';
import { BASE_DATAZOOM } from '@/utils/charts';
import type { EChartsOption } from 'echarts';

const GRANULARITY_OPTIONS = [
  { label: '96点', value: '96点' },
  { label: '24小时', value: '24小时' },
  { label: '天', value: '天' },
];

const PRICE_RULES: Record<string, [string, string]> = {
  '实时市场节点电价': ['实时电能量电费', '实际上网电量'],
  '日前市场出清电价': ['日前电能量电费', '日前出清电量'],
  '中长期合约固定价格': ['中长期合约电费', '中长期合约电量'],
  '中长期结算参考点现货市场电价': ['中长期合约电费', '中长期合约电量'],
};

function downloadCSV(columns: { title: string; dataIndex: string }[], rows: Record<string, unknown>[], filename: string) {
  const header = columns.map((c) => c.title).join(',');
  const body = rows
    .map((row) => columns.map((c) => {
      const v = row[c.dataIndex];
      if (v == null) return '';
      return String(v);
    }).join(','))
    .join('\n');
  const bom = '﻿';
  const blob = new Blob([bom + header + '\n' + body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const DailyClearingTab: React.FC = () => {
  const isMobile = useIsMobile();

  const today = dayjs();
  const weekAgo = today.subtract(7, 'day');
  const [dates, setDates] = useState<[Dayjs, Dayjs]>([weekAgo, today]);
  const [granularity, setGranularity] = useState('天');
  const [mergeUnits, setMergeUnits] = useState(false);

  const [selectedUnits, setSelectedUnits] = useState<string[]>([]);
  const [selectedL1, setSelectedL1] = useState<string[]>([]);
  const [selectedL2, setSelectedL2] = useState<string[]>([]);

  const startDateStr = dates[0].format('YYYY-MM-DD');
  const endDateStr = dates[1].format('YYYY-MM-DD');

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useSettlementData({
    start_date: startDateStr,
    end_date: endDateStr,
    granularity,
    merge_units: mergeUnits,
    units: selectedUnits.length > 0 ? selectedUnits : undefined,
  });

  const rows: Record<string, string | number>[] = data?.data?.rows ?? [];

  const { baseCols, metricCols, l1Options, l2Options, allUnits } = useMemo(() => {
    if (!rows.length) {
      return { baseCols: [], metricCols: [], l1Options: [], l2Options: [], allUnits: [] };
    }
    const keys = Object.keys(rows[0]);
    const base: string[] = [];
    const metric: string[] = [];
    const l1Set = new Set<string>();
    const l2Set = new Set<string>();
    const unitSet = new Set<string>();

    for (const k of keys) {
      if (k.includes('_')) {
        metric.push(k);
        const parts = k.split('_');
        l1Set.add(parts[0]);
        l2Set.add(parts.slice(1).join('_'));
      } else {
        base.push(k);
        if (k === '单元名称') {
          for (const r of rows) {
            const v = String(r[k] ?? '');
            if (v) unitSet.add(v);
          }
        }
      }
    }

    return {
      baseCols: base,
      metricCols: metric,
      l1Options: Array.from(l1Set).sort(),
      l2Options: Array.from(l2Set).sort(),
      allUnits: Array.from(unitSet).sort(),
    };
  }, [rows]);

  const prevMerge = React.useRef(mergeUnits);

  // When mergeUnits changes, clear unit selection (units may differ after merge)
  React.useEffect(() => {
    if (prevMerge.current !== mergeUnits) {
      prevMerge.current = mergeUnits;
      setSelectedUnits([]);
    }
  }, [mergeUnits]);

  const visibleMetricKeys = useMemo(
    () =>
      metricCols.filter((k) => {
        const parts = k.split('_');
        const l1 = parts[0];
        const l2 = parts.slice(1).join('_');
        return selectedL1.includes(l1) && selectedL2.includes(l2);
      }),
    [metricCols, selectedL1, selectedL2],
  );

  const filteredRows = useMemo(() => {
    let result = rows;
    if (selectedUnits.length > 0) {
      const unitKey = '单元名称';
      result = result.filter((r) => {
        const realName = String(r[unitKey] ?? '');
        return selectedUnits.includes(realName);
      });
    }
    return result;
  }, [rows, selectedUnits]);

  // Build Ant Design table columns with L1 grouping
  const tableColumns: ColumnsType<Record<string, unknown>> = useMemo(() => {
    const cols: ColumnsType<Record<string, unknown>> = [];

    // Base columns — fixed left
    const fixedLeftBase = ['日期', '单元名称', '完整时间', '时段'];
    for (const key of baseCols) {
      const isFixed = fixedLeftBase.includes(key);
      const isTime = key === '完整时间';
      cols.push({
        title: key,
        dataIndex: key,
        key,
        fixed: isFixed ? ('left' as const) : undefined,
        width: isFixed ? (key === '单元名称' ? 140 : 100) : 100,
        ellipsis: true,
        render: (v: unknown) => {
          if (v == null || v === '') return '-';
          if (isTime && typeof v === 'string' && v.length > 10) return v.slice(0, 10);
          return String(v);
        },
      });
    }

    // Metric columns grouped by L1
    const l1Groups = new Map<string, string[]>();
    for (const key of visibleMetricKeys) {
      const parts = key.split('_');
      const l1 = parts[0];
      if (!l1Groups.has(l1)) l1Groups.set(l1, []);
      l1Groups.get(l1)!.push(key);
    }

    for (const [l1, keys] of l1Groups) {
      const children: ColumnsType<Record<string, unknown>> = keys.map((key) => ({
        title: key.split('_').slice(1).join('-'),
        dataIndex: key,
        key,
        width: 120,
        ellipsis: true,
        align: 'right' as const,
        render: (v: unknown) => {
          if (v == null || v === '') return '-';
          if (typeof v === 'number') return format2(v);
          return String(v);
        },
      }));

      if (children.length === 1) {
        cols.push({ ...children[0], title: `${l1}-${children[0].title}` });
      } else {
        cols.push({
          title: l1,
          key: `group-${l1}`,
          children,
        });
      }
    }

    return cols;
  }, [baseCols, visibleMetricKeys]);

  // Summary row for the table: sum visible numeric columns, recalculate prices
  const tableSummary = useMemo(() => {
    if (filteredRows.length === 0) return null;
    const summary: Record<string, number> = {};
    const allKeys = [...baseCols, ...visibleMetricKeys];
    // Check any row for numeric type (not just first row, which might have nulls)
    const hasNumber = new Set<string>();
    for (const r of filteredRows) {
      for (const k of allKeys) {
        if (!hasNumber.has(k) && typeof r[k] === 'number') {
          hasNumber.add(k);
        }
      }
    }
    for (const key of allKeys) {
      if (hasNumber.has(key)) {
        summary[key] = filteredRows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
      }
    }
    // Recalculate price columns from cost/vol
    for (const key of visibleMetricKeys) {
      const l2 = key.split('_').slice(1).join('_');
      const rule = PRICE_RULES[l2];
      if (rule) {
        const [costL2, volL2] = rule;
        const parts = key.split('_');
        const l1 = parts[0];
        const costKey = `${l1}_${costL2}`;
        const volKey = `${l1}_${volL2}`;
        if (costKey in summary && volKey in summary && summary[volKey] !== 0) {
          summary[key] = summary[costKey] / summary[volKey];
        }
      }
    }
    return summary;
  }, [filteredRows, baseCols, visibleMetricKeys]);

  const chartOption: EChartsOption = useMemo(() => {
    if (!filteredRows.length) return {};
    const timeKey = '完整时间';
    if (visibleMetricKeys.length === 0) return {};

    const times = filteredRows.map((r) => String(r[timeKey] ?? '')).filter(Boolean);
    const colorPalette = ['#0d9488', '#52c41a', '#faad14', '#f5222d', '#13c2c2', '#722ed1', '#eb2f96', '#fa8c16'];

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        valueFormatter: (value: unknown) => (typeof value === 'number' ? format2(value) : String(value)),
      },
      toolbox: { show: false },
      dataZoom: BASE_DATAZOOM,
      legend: { bottom: 50, type: 'scroll' as const },
      grid: { left: 50, right: 20, top: 30, bottom: 110 },
      xAxis: {
        type: 'category',
        data: times,
        axisLabel: { fontSize: 11, color: '#9ca3af', rotate: 30 },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 11, color: '#9ca3af' },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
      },
      series: visibleMetricKeys.map((m, i) => ({
        name: m.replace(/_/g, '-'),
        type: 'line',
        data: filteredRows.map((r) => (typeof r[m] === 'number' ? r[m] : null)),
        smooth: true,
        showSymbol: false,
        connectNulls: true,
        itemStyle: { color: colorPalette[i % colorPalette.length] },
        lineStyle: { width: 2 },
      })),
    };
  }, [filteredRows, visibleMetricKeys]);

  const handleExportCSV = () => {
    const flatCols: { title: string; dataIndex: string }[] = [];
    for (const col of tableColumns) {
      if ('children' in col && col.children) {
        for (const child of col.children) {
          flatCols.push({
            title: String(child.title ?? ''),
            dataIndex: String((child as { dataIndex?: string }).dataIndex ?? ''),
          });
        }
      } else {
        flatCols.push({
          title: String(col.title ?? ''),
          dataIndex: String((col as { dataIndex?: string }).dataIndex ?? ''),
        });
      }
    }
    const exportData = tableSummary
      ? [...filteredRows, { ...tableSummary, 日期: '合计', 单元名称: '' }]
      : filteredRows;
    downloadCSV(flatCols, exportData, `日清分_${startDateStr}_${endDateStr}.csv`);
  };

  return (
    <div>
      {/* Filter Bar */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>日期范围</div>
            <DatePicker.RangePicker
              value={dates}
              onChange={(vals) => {
                if (vals && vals[0] && vals[1]) setDates([vals[0], vals[1]]);
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>时间颗粒度</div>
            <Radio.Group
              options={GRANULARITY_OPTIONS}
              value={granularity}
              onChange={(e) => setGranularity(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>展示模式</div>
            <Radio.Group
              value={mergeUnits}
              onChange={(e) => setMergeUnits(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value={false}>分单元</Radio.Button>
              <Radio.Button value={true}>全厂汇总</Radio.Button>
            </Radio.Group>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>&nbsp;</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="primary" icon={<SearchOutlined />} onClick={() => refetch()} style={{ flex: 1 }}>
                查询
              </Button>
              {rows.length > 0 && (
                <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
                  导出 CSV
                </Button>
              )}
            </div>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>场站筛选</div>
            <Select
              mode="multiple"
              value={selectedUnits}
              options={allUnits.map((u) => ({ value: u, label: u }))}
              onChange={setSelectedUnits}
              style={{ width: '100%' }}
              placeholder={mergeUnits ? '已选单元将汇总展示' : '选择场站/单元'}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>一级科目</div>
            <Select
              mode="multiple"
              value={selectedL1}
              options={l1Options.map((l) => ({ value: l, label: l }))}
              onChange={(v) => {
                setSelectedL1(v);
                const availableL2 = new Set<string>();
                for (const k of metricCols) {
                  const parts = k.split('_');
                  if (parts.length >= 2 && v.includes(parts[0])) {
                    availableL2.add(parts.slice(1).join('_'));
                  }
                }
                setSelectedL2(Array.from(availableL2));
              }}
              style={{ width: '100%' }}
              placeholder="选择一级科目"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>二级指标</div>
            <Select
              mode="multiple"
              value={selectedL2}
              options={l2Options.map((l) => ({ value: l, label: l }))}
              onChange={setSelectedL2}
              style={{ width: '100%' }}
              placeholder="选择二级指标"
            />
          </Col>
        </Row>
      </div>

      {/* Error */}
      {error && (
        <Alert
          message={error instanceof Error ? error.message : '请求失败'}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" icon={<ReloadOutlined />} onClick={() => refetch()}>
              重试
            </Button>
          }
        />
      )}

      {/* Empty */}
      {!isLoading && !error && rows.length === 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, textAlign: 'center' }}>
          <Empty
            description={`${startDateStr} 至 ${endDateStr} 期间暂无入库记录。`}
            style={{ marginBottom: 16 }}
          />
          <Alert
            message="请前往「场站数据上传 → 日清分上传」完成初始数据导入，或扩大日期范围重试。"
            type="info"
            showIcon
            style={{ maxWidth: 600, margin: '0 auto 16px' }}
          />
        </div>
      )}

      {/* Table */}
      {rows.length > 0 && (
        <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 16 } }}>
          <Table
            columns={tableColumns}
            dataSource={filteredRows.map((row, idx) => ({ ...row, _key: idx }))}
            rowKey="_key"
            loading={isLoading}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
            scroll={{ x: isMobile ? 'max-content' : (tableColumns.length > 8 ? 'max-content' : undefined) }}
            size="small"
            bordered
            summary={() => {
              if (!tableSummary) return null;
              // Flatten to leaf columns only: group headers don't occupy data positions
              const flatCols: Record<string, unknown>[] = [];
              for (const col of tableColumns) {
                if ('children' in col && col.children) {
                  for (const child of col.children) {
                    flatCols.push(child as unknown as Record<string, unknown>);
                  }
                } else {
                  flatCols.push(col as unknown as Record<string, unknown>);
                }
              }
              return (
                <Table.Summary.Row>
                  {flatCols.map((col, idx) => {
                    const dataIndex = (col.dataIndex as string) || null;
                    if (!dataIndex) {
                      return <Table.Summary.Cell key={idx} index={idx}>-</Table.Summary.Cell>;
                    }
                    const val = tableSummary[dataIndex];
                    if (val === undefined || dataIndex === '完整时间' || dataIndex === '时段') {
                      return (
                        <Table.Summary.Cell key={idx} index={idx}>
                          {idx === 0 ? '合计' : dataIndex === '单元名称' ? `全量 (${filteredRows.length}条)` : '-'}
                        </Table.Summary.Cell>
                      );
                    }
                    return (
                      <Table.Summary.Cell key={idx} index={idx}>
                        <strong>{format2(val)}</strong>
                      </Table.Summary.Cell>
                    );
                  })}
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      )}

      {/* Chart */}
      {rows.length > 0 && selectedL1.length > 0 && selectedL2.length > 0 && (
        <ChartCard
          title="结算趋势分析"
          option={chartOption}
          loading={isLoading}
          error={null}
          height={isMobile ? 250 : 400}
        />
      )}
    </div>
  );
};

export default DailyClearingTab;
